import { Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { type JSX } from 'react';

type Size = 's' | 'm';

type Variant = 'standard' | 'inverted' | 'featured';

const variantColors: Record<Variant, string[]> = {
  standard: [
    'bg-primary',
    'border-primary',
    'text-white',
    'hover:bg-primary-dark',
    'hover:border-primary-dark',
  ],
  inverted: [
    'bg-transparent',
    'border-primary',
    'text-primary',
    'hover:bg-primary-dark',
    'hover:border-primary-dark',
    'hover:text-white',
  ],
  featured: [
    'bg-featured',
    'border-featured',
    'text-white',
    'hover:bg-featured-hover',
    'hover:border-featured-hover',
  ],
};

function getClasses(size: Size, variant: Variant, fullWidth: boolean) {
  const baseClasses = [
    'flex',
    'gap-8',
    'items-center',
    'rounded-4',
    'border',
    'border-solid',
    'font-600',
    'text-14',
    'disabled:text-typography-disabled',
    'disabled:bg-neutral-200',
    'disabled:border-neutral-200',
    'transition-opacity',
    'transition-colors',
    'whitespace-nowrap',
  ];

  baseClasses.push(...variantColors[variant]);
  baseClasses.push(...(size === 's' ? ['px-16', 'py-6'] : ['px-16', 'py-12']));

  if (fullWidth) baseClasses.push('w-full');

  return baseClasses.join(' ');
}

type PrimaryButtonProps = {
  cb: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  disabledText?: string;
  label: string;
  inverted?: boolean;
  variant?: Variant;
  loading?: boolean;
  size?: Size;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
};

export default function PrimaryButton({
  cb,
  disabled,
  disabledText,
  label,
  inverted,
  variant,
  loading,
  size = 's',
  type = 'button',
  fullWidth,
  children,
  icon,
}: PrimaryButtonProps): JSX.Element {
  const resolvedVariant: Variant = variant ?? (inverted ? 'inverted' : 'standard');
  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (type !== 'submit') e.preventDefault();
    e.stopPropagation();
    cb(e);
  }

  if (disabled && disabledText) {
    return (
      <DisabledWrapper disabledText={disabledText}>
        <Button
          onClick={handleClick}
          disabled
          className={getClasses(size, resolvedVariant, fullWidth ?? false)}
          type={type}
        >
          {icon && <span className="flex items-center h-[20px]">{icon}</span>}
          {children || label}
        </Button>
      </DisabledWrapper>
    );
  }

  return (
    <Button
      onClick={handleClick}
      disabled={loading || disabled}
      className={getClasses(size, resolvedVariant, fullWidth ?? false)}
      type={type}
    >
      {loading && <CircularProgress size={20} />}
      {icon && <span className="flex items-center h-[20px]">{icon}</span>}
      {children || label}
    </Button>
  );
}

function DisabledWrapper({
  children,
  disabledText,
}: {
  children: React.ReactNode;
  disabledText: string;
}) {
  return (
    <Tooltip title={<Typography variant="body1">{disabledText}</Typography>} arrow>
      <div className="flex flex-col">{children}</div>
    </Tooltip>
  );
}
